import { api } from "./client";
import type { components } from "./types";

export type User = components["schemas"]["User"];
export type UserRole = components["schemas"]["UserRole"];
export type WorkType = components["schemas"]["WorkType"];
export type WorkTypePositionIndex = components["schemas"]["WorkTypePositionIndex"];
export type CreateWorkTypeRequest = components["schemas"]["CreateWorkTypeRequest"];
export type PatchWorkTypeRequest = components["schemas"]["PatchWorkTypeRequest"];
export type EquipmentType = components["schemas"]["EquipmentType"];
export type EquipmentTypeElement = components["schemas"]["EquipmentTypeElement"];
export type EquipmentTypeElementStatus = components["schemas"]["EquipmentTypeElementStatus"];
export type AssemblyElement = components["schemas"]["AssemblyElement"];
export type CreateEquipmentTypeRequest = components["schemas"]["CreateEquipmentTypeRequest"];
export type PatchEquipmentTypeRequest = components["schemas"]["PatchEquipmentTypeRequest"];
export type CreateAssemblyElementRequest = components["schemas"]["CreateAssemblyElementRequest"];
export type PatchAssemblyElementRequest = components["schemas"]["PatchAssemblyElementRequest"];
export type MetricType = components["schemas"]["MetricType"];
export type AssemblyElementMetric = components["schemas"]["AssemblyElementMetric"];
export type AssemblyElementMetricOption = components["schemas"]["AssemblyElementMetricOption"];
export type MetricWithOptions = components["schemas"]["MetricWithOptions"];
export type CreateMetricRequest = components["schemas"]["CreateMetricRequest"];
export type PatchMetricRequest = components["schemas"]["PatchMetricRequest"];
export type PeopleCapabilities = components["schemas"]["PeopleCapabilities"];
export type AdminPersonEventType = components["schemas"]["AdminPersonEventType"];
export type CreatePersonEventTypeRequest = components["schemas"]["CreatePersonEventTypeRequest"];
export type PatchPersonEventTypeRequest = components["schemas"]["PatchPersonEventTypeRequest"];

/**
 * People-management capabilities of the current actor — drives the
 * data-driven `/people` screen (entry / CTA / actions / subtitle) instead of
 * branching on `role === "admin"`. A non-manager gets a 200 with empty caps.
 */
export function getPeopleCapabilities(): Promise<PeopleCapabilities> {
  return api.get<PeopleCapabilities>("/people/capabilities");
}

export function listAdminWorkTypes(): Promise<WorkType[]> {
  return api.get<WorkType[]>("/admin/work-types");
}

export function createAdminWorkType(body: CreateWorkTypeRequest): Promise<WorkType> {
  return api.post<WorkType>("/admin/work-types", body);
}

export function patchAdminWorkType(id: number, body: PatchWorkTypeRequest): Promise<WorkType> {
  return api.patch<WorkType>(`/admin/work-types/${id}`, body);
}

export function deleteAdminWorkType(id: number): Promise<void> {
  return api.delete<void>(`/admin/work-types/${id}`);
}

export function reorderAdminWorkTypes(orderedIds: number[]): Promise<void> {
  return api.post<void>("/admin/work-types/reorder", { ordered_ids: orderedIds });
}

export function getWorkTypePositionIds(workTypeId: number): Promise<number[]> {
  return api.get<number[]>(`/admin/work-types/${workTypeId}/positions`);
}

export function listWorkTypePositionIndex(): Promise<WorkTypePositionIndex[]> {
  return api.get<WorkTypePositionIndex[]>("/admin/work-types/position-index");
}

export function setWorkTypePositionIds(workTypeId: number, ids: number[]): Promise<number[]> {
  return api.put<number[]>(`/admin/work-types/${workTypeId}/positions`, { ids });
}

export function listAdminPersonEventTypes(): Promise<AdminPersonEventType[]> {
  return api.get<AdminPersonEventType[]>("/admin/person-event-types");
}

export function createAdminPersonEventType(
  body: CreatePersonEventTypeRequest,
): Promise<AdminPersonEventType> {
  return api.post<AdminPersonEventType>("/admin/person-event-types", body);
}

export function patchAdminPersonEventType(
  id: number,
  body: PatchPersonEventTypeRequest,
): Promise<AdminPersonEventType> {
  return api.patch<AdminPersonEventType>(`/admin/person-event-types/${id}`, body);
}

export function deleteAdminPersonEventType(id: number): Promise<void> {
  return api.delete<void>(`/admin/person-event-types/${id}`);
}

export function reorderAdminPersonEventTypes(orderedIds: number[]): Promise<void> {
  return api.post<void>("/admin/person-event-types/reorder", { ordered_ids: orderedIds });
}

// ── equipment types (виды оборудования) ──────────────────────────────────

export function listAdminEquipmentTypes(): Promise<EquipmentType[]> {
  return api.get<EquipmentType[]>("/admin/equipment-types");
}

export function createAdminEquipmentType(body: CreateEquipmentTypeRequest): Promise<EquipmentType> {
  return api.post<EquipmentType>("/admin/equipment-types", body);
}

export function patchAdminEquipmentType(
  id: number,
  body: PatchEquipmentTypeRequest,
): Promise<EquipmentType> {
  return api.patch<EquipmentType>(`/admin/equipment-types/${id}`, body);
}

export function deleteAdminEquipmentType(id: number): Promise<void> {
  return api.delete<void>(`/admin/equipment-types/${id}`);
}

export function reorderAdminEquipmentTypes(orderedIds: number[]): Promise<void> {
  return api.post<void>("/admin/equipment-types/reorder", { ordered_ids: orderedIds });
}

export function listEquipmentTypeElements(typeId: number): Promise<EquipmentTypeElement[]> {
  return api.get<EquipmentTypeElement[]>(`/admin/equipment-types/${typeId}/elements`);
}

export function setEquipmentTypeElementStatus(
  typeId: number,
  elementId: number,
  status: EquipmentTypeElementStatus,
): Promise<EquipmentTypeElement> {
  return api.put<EquipmentTypeElement>(`/admin/equipment-types/${typeId}/elements/${elementId}`, {
    status,
  });
}

export function removeEquipmentTypeElement(typeId: number, elementId: number): Promise<void> {
  return api.delete<void>(`/admin/equipment-types/${typeId}/elements/${elementId}`);
}

// ── assembly elements (глобальный каталог — точка истины) ─────────────────
// §2.2 нужны только чтение каталога + create (inline-add); §2.3 добавляет
// полный CRUD элементов + типизированные метрики и их варианты.

export function listAdminAssemblyElements(): Promise<AssemblyElement[]> {
  return api.get<AssemblyElement[]>("/admin/assembly-elements");
}

export function createAdminAssemblyElement(
  body: CreateAssemblyElementRequest,
): Promise<AssemblyElement> {
  return api.post<AssemblyElement>("/admin/assembly-elements", body);
}

export function patchAdminAssemblyElement(
  id: number,
  body: PatchAssemblyElementRequest,
): Promise<AssemblyElement> {
  return api.patch<AssemblyElement>(`/admin/assembly-elements/${id}`, body);
}

export function deleteAdminAssemblyElement(id: number): Promise<void> {
  return api.delete<void>(`/admin/assembly-elements/${id}`);
}

export function reorderAdminAssemblyElements(orderedIds: number[]): Promise<void> {
  return api.post<void>("/admin/assembly-elements/reorder", { ordered_ids: orderedIds });
}

// ── метрики элемента (assembly_element_metrics) + варианты ────────────────
// Метрики принадлежат элементу и одинаковы для него на всех типах (§2.9).
// `metric_type` неизменяем после создания — в Patch его нет.

export function listAssemblyElementMetrics(elementId: number): Promise<MetricWithOptions[]> {
  return api.get<MetricWithOptions[]>(`/admin/assembly-elements/${elementId}/metrics`);
}

export function createAssemblyElementMetric(
  elementId: number,
  body: CreateMetricRequest,
): Promise<AssemblyElementMetric> {
  return api.post<AssemblyElementMetric>(`/admin/assembly-elements/${elementId}/metrics`, body);
}

export function patchAssemblyElementMetric(
  metricId: number,
  body: PatchMetricRequest,
): Promise<AssemblyElementMetric> {
  return api.patch<AssemblyElementMetric>(`/admin/assembly-elements/metrics/${metricId}`, body);
}

export function deleteAssemblyElementMetric(metricId: number): Promise<void> {
  return api.delete<void>(`/admin/assembly-elements/metrics/${metricId}`);
}

export function reorderAssemblyElementMetrics(
  elementId: number,
  orderedIds: number[],
): Promise<void> {
  return api.post<void>(`/admin/assembly-elements/${elementId}/metrics/reorder`, {
    ordered_ids: orderedIds,
  });
}

export function replaceMetricOptions(
  metricId: number,
  values: string[],
): Promise<AssemblyElementMetricOption[]> {
  return api.put<AssemblyElementMetricOption[]>(
    `/admin/assembly-elements/metrics/${metricId}/options`,
    { values },
  );
}

// ---------------------------------------------------------------------------
// Каталог контрольных точек вида работ (задача 114 §1.4 / §2.3)
// ---------------------------------------------------------------------------

export type ChecklistCatalogItem = components["schemas"]["ChecklistCatalogItem"];
export type ChecklistItemCondition = components["schemas"]["WorkTypeChecklistItemCondition"];
/** Условие с разрешёнными именами справочников — то, что отдаёт каталог (§2.3). */
export type ChecklistConditionView = components["schemas"]["ChecklistConditionView"];

export function listChecklistItems(workTypeId: number): Promise<ChecklistCatalogItem[]> {
  return api.get<ChecklistCatalogItem[]>(`/admin/work-types/${workTypeId}/checklist-items`);
}

export function createChecklistItem(
  workTypeId: number,
  title: string,
): Promise<ChecklistCatalogItem> {
  return api.post<ChecklistCatalogItem>(`/admin/work-types/${workTypeId}/checklist-items`, {
    title,
  });
}

export function patchChecklistItem(
  itemId: number,
  body: { title?: string; is_active?: boolean },
): Promise<ChecklistCatalogItem> {
  return api.patch<ChecklistCatalogItem>(`/admin/work-types/checklist-items/${itemId}`, body);
}

export function deleteChecklistItem(itemId: number): Promise<void> {
  return api.delete<void>(`/admin/work-types/checklist-items/${itemId}`);
}

export function reorderChecklistItems(workTypeId: number, orderedIds: number[]): Promise<void> {
  return api.post<void>(`/admin/work-types/${workTypeId}/checklist-items/reorder`, {
    ordered_ids: orderedIds,
  });
}

/** Условие пункта: вид оборудования и/или элемент сборки (хотя бы одно поле). */
export function addChecklistItemCondition(
  itemId: number,
  body: { equipment_type_id?: number | null; assembly_element_id?: number | null },
): Promise<ChecklistItemCondition> {
  return api.post<ChecklistItemCondition>(
    `/admin/work-types/checklist-items/${itemId}/conditions`,
    body,
  );
}

export function deleteChecklistItemCondition(itemId: number, conditionId: number): Promise<void> {
  return api.delete<void>(`/admin/work-types/checklist-items/${itemId}/conditions/${conditionId}`);
}
